// Error handling utilities for better user feedback

export interface FirebaseError {
  code: string;
  message: string;
}

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    // Handle Firebase Auth errors
    if ('code' in error) {
      const firebaseError = error as FirebaseError;
      switch (firebaseError.code) {
        case 'auth/user-not-found':
          return 'User account not found. Please check your email and try again.';
        case 'auth/wrong-password':
          return 'Incorrect password. Please try again.';
        case 'auth/email-already-in-use':
          return 'An account with this email already exists.';
        case 'auth/weak-password':
          return 'Password is too weak. Please choose a stronger password.';
        case 'auth/invalid-email':
          return 'Invalid email address. Please check your email format.';
        case 'auth/network-request-failed':
          return 'Network error. Please check your internet connection and try again.';
        case 'auth/too-many-requests':
          return 'Too many failed attempts. Please try again later.';
        case 'auth/user-disabled':
          return 'This account has been disabled. Please contact support.';
        case 'auth/operation-not-allowed':
          return 'This operation is not allowed. Please contact support.';
        case 'auth/invalid-credential':
          return 'Invalid credentials. Please check your email and password.';
        default:
          return firebaseError.message || 'An authentication error occurred.';
      }
    }

    // Handle date/time related errors
    if (error.message.includes('Invalid Date') || error.message.includes('toDate')) {
      return 'There was an issue with date formatting. Please try refreshing the page.';
    }
    if (error.message.includes('timestamp') || error.message.includes('date')) {
      return 'Date processing error. Please try again.';
    }

    // Handle Firestore errors
    if (error.message.includes('permission')) {
      return 'You don\'t have permission to perform this action. Please check your authentication.';
    }
    if (error.message.includes('network')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    if (error.message.includes('quota')) {
      return 'Service temporarily unavailable. Please try again later.';
    }
    if (error.message.includes('unavailable')) {
      return 'Service is currently unavailable. Please try again later.';
    }

    // Handle general errors
    return error.message;
  }

  // Handle non-Error objects
  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred. Please try again.';
};

export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return error.message.includes('network') || 
           error.message.includes('fetch') ||
           error.message.includes('connection');
  }
  return false;
};

export const isAuthError = (error: unknown): boolean => {
  if (error instanceof Error && 'code' in error) {
    const firebaseError = error as FirebaseError;
    return firebaseError.code.startsWith('auth/');
  }
  return false;
};

export const isFirestoreError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return error.message.includes('permission') ||
           error.message.includes('quota') ||
           error.message.includes('unavailable');
  }
  return false;
}; 